import { agingBucket } from '@jobos/shared';
import type {
  AgingBucket,
  Invoice,
  InvoiceKind,
  InvoiceLifecycle,
  InvoiceSendState,
  InvoiceSource,
  WorkspaceInvoiceRow,
} from '@jobos/shared';

/**
 * What the shared invoice components render.
 *
 * The workspace's server rows satisfy it directly; the job Billing tab adapts
 * its `Invoice[]` with `fromInvoice`. The job/client identity fields are
 * optional because the job-scoped tab has no use for columns naming the job it
 * is already on.
 *
 * Nothing here is recomputed in the browser — lifecycle, overdue and aging all
 * arrive from the server (spec D6). `fromInvoice` is an adaptation of fields
 * the server already decided, not a second derivation of them.
 */
export interface BillingInvoiceRow {
  invoiceId: string;
  invoiceNumber: string;
  jobId: string;
  kind: InvoiceKind;
  /**
   * How the invoice got its figures. 'lineItems' partials were cut from
   * breakdown lines and so can be rebuilt from them; 'custom' ones are an
   * amount and a description someone typed, and are edited instead. The action
   * menu picks between Regenerate and the edit on exactly this.
   */
  source: InvoiceSource;
  origin: 'native' | 'external';
  isCash: boolean;
  lifecycle: InvoiceLifecycle;
  amount: number;
  collected: number;
  outstanding: number;
  invoiceDate: string;
  /**
   * Derived server-side from the date the document bears (the sent date once
   * there is one, else the invoice date) + terms; null when it has none.
   */
  dueDate: string | null;
  sentAt: string | null;
  paidAt?: string | null;
  notes: string | null;
  hasPdf: boolean;
  /** A migrated receivable of unknown size — never shown as $0.00 (spec §5). */
  amountUnknown: boolean;
  daysOutstanding: number | null;
  agingBucket: AgingBucket | null;
  isOverdue: boolean;
  jobCode?: string;
  jobDescription?: string | null;
  clientId?: string;
  clientName?: string;
  /**
   * The newest send attempt's outcome (FR-4.6). Optional so the row factories
   * and mocks that build this shape by hand keep compiling; both feeds below
   * populate it, which is the point — a badge fed by only one of them appears
   * in /invoices and silently never on the job page.
   */
  lastSendState?: InvoiceSendState | null;
  lastSendDetail?: string | null;
}

/** The workspace's rows are already this shape. */
export const fromWorkspaceRow = (r: WorkspaceInvoiceRow): BillingInvoiceRow => r;

/**
 * The job Billing tab's rows.
 *
 * `daysOutstanding` and `isOverdue` are absent from the `Invoice` payload —
 * the job-scoped list has no aging column, so they are left null/false rather
 * than computed here. Computing them would be exactly the client-side date
 * arithmetic spec D6 forbids, and it would be wrong: overdue depends on the
 * client's own threshold, which this payload does not carry.
 */
export function fromInvoice(inv: Invoice): BillingInvoiceRow {
  return {
    invoiceId: inv.id,
    invoiceNumber: inv.invoiceNumber,
    jobId: inv.jobId,
    kind: inv.kind,
    source: inv.source,
    origin: inv.origin,
    isCash: inv.isCash,
    lifecycle: inv.lifecycle,
    amount: inv.amount,
    collected: inv.collected,
    outstanding: inv.outstanding,
    invoiceDate: inv.invoiceDate,
    // Unlike daysOutstanding/isOverdue below, this one IS on the payload —
    // formatInvoice derives it, so the job tab shows the same date the
    // workspace does rather than recomputing it here (spec D6).
    dueDate: inv.dueDate,
    sentAt: inv.sentAt,
    paidAt: inv.paidAt,
    notes: inv.notes,
    // Only partials are ever cached, and a native invoice regenerates on
    // demand; what has no document at all is a backfilled external row.
    hasPdf: inv.origin === 'native' || inv.pdfGeneratedAt !== null,
    amountUnknown: inv.origin === 'external' && inv.amount === 0,
    daysOutstanding: null,
    agingBucket: null,
    isOverdue: false,
    // Unlike the aging fields, this one IS on the payload — the server resolves
    // the newest send per invoice in formatInvoices. Normalised to null so a
    // legacy payload without the field reads the same as a never-sent invoice.
    lastSendState: inv.lastSendState ?? null,
    lastSendDetail: inv.lastSendDetail ?? null,
  };
}

/** Which optional columns a caller wants. The workspace shows more than the job tab. */
export interface InvoiceColumns {
  client?: boolean;
  job?: boolean;
  aging?: boolean;
  collected?: boolean;
  paidDate?: boolean;
  notes?: boolean;
  /**
   * The bounce badge (FR-4.6). Its OWN flag rather than a ride on `aging`,
   * because `JOB_COLUMNS` omits aging entirely and `fromInvoice` hardcodes
   * `agingBucket: null` — a badge tied to that cell would appear in /invoices
   * and silently never on the job Billing tab, which is the surface a manager
   * opens per job.
   *
   * It occupies no column of its own: it sits beside the lifecycle badge in
   * the Status cell, which both column sets always render. A dedicated column
   * would be blank on every healthy row and would have to be kept in step with
   * `columnCount` in InvoiceTable.
   */
  sendOutcome?: boolean;
  /**
   * The date the invoice went out (NEW-231). Both surfaces set it: a manager
   * asking "did this go out, and when?" asks it per job and across the board,
   * and the answer lives in one column either way. Blank on a never-sent row
   * rather than hidden — "no date" IS the answer there.
   */
  sentDate?: boolean;
}

export const WORKSPACE_COLUMNS: InvoiceColumns = {
  client: true,
  job: true,
  aging: true,
  collected: true,
  sendOutcome: true,
  sentDate: true,
};

export const JOB_COLUMNS: InvoiceColumns = {
  collected: true,
  paidDate: true,
  notes: true,
  sendOutcome: true,
  sentDate: true,
};

/**
 * Re-exported so a consumer that only has `daysOutstanding` (a mock, a test)
 * can label it the same way the server would. Production rows carry the
 * server's bucket and this is not called.
 */
export { agingBucket };
